import { useState, type FormEvent } from "react";
import { TestTube, ArrowRight, ExternalLink } from "lucide-react";
import { testConsoleApi } from "@/features/test-console";
import type { TestResolveResponse } from "@/features/test-console";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

export function TestConsolePage() {
  const [shortCode, setShortCode] = useState("");
  const [result, setResult] = useState<TestResolveResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function handleResolve(e: FormEvent) {
    e.preventDefault();
    if (!shortCode.trim()) return;

    setLoading(true);
    setError("");
    setResult(null);

    try {
      const data = await testConsoleApi.resolve(shortCode.trim());
      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to resolve link");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Test Console</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Dry-run link resolution without consuming personal links
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <TestTube className="h-5 w-5" />
            Resolve Link
          </CardTitle>
          <CardDescription>
            Enter a short code to see where it resolves to. This does not count as a click.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleResolve} className="flex gap-3">
            <Input
              placeholder="Enter short code (e.g., Ab12Zx)"
              value={shortCode}
              onChange={(e) => setShortCode(e.target.value)}
              className="font-mono"
            />
            <Button type="submit" disabled={loading || !shortCode.trim()}>
              {loading ? "Resolving..." : "Resolve"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {error && (
        <Card className="border-red-200 dark:border-red-700">
          <CardContent className="p-4">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </CardContent>
        </Card>
      )}

      {result && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Resolution Result</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3 rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
              <span className="font-mono text-sm font-medium text-gray-700 dark:text-gray-300">{result.shortCode}</span>
              <ArrowRight className="h-4 w-4 text-gray-400 dark:text-gray-500" />
              <a
                href={result.resolvedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:underline dark:text-blue-400"
              >
                {result.resolvedUrl}
                <ExternalLink className="ml-1 inline h-3 w-3" />
              </a>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Link Type</p>
                <Badge variant={result.linkType === "General" ? "default" : "secondary"}>
                  {result.linkType}
                </Badge>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Status</p>
                <Badge variant={result.status === "Active" ? "success" : "warning"}>
                  {result.status}
                </Badge>
              </div>
            </div>

            {result.utmParameters && Object.keys(result.utmParameters).length > 0 && (
              <div>
                <p className="mb-2 text-sm font-medium text-gray-500 dark:text-gray-400">UTM Parameters Applied</p>
                <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-800">
                  {Object.entries(result.utmParameters).map(([key, value]) => (
                    <div key={key} className="flex justify-between text-sm">
                      <span className="font-mono text-gray-500 dark:text-gray-400">{key}</span>
                      <span className="text-gray-900 dark:text-gray-100">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Destination URL (before UTM)</p>
              <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{result.destinationUrl}</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
